/**
 * Retail area (/retail/...): the frame around today's Retail pages, plus the section landing pages.
 * The pages themselves stay in src/modules (their URLs moved under /retail on 2026-10-16; App.tsx
 * redirects every old path). Since 0.53.0 the sections and pages are in the shell's sidebar.
 */

import { Link, useParams } from 'react-router';
import { EmptyState, PageHeader } from '@retail-ops/panel-shell';
import { useAuth } from '@/contexts/AuthContext';
import { usePlatforms } from '@/contexts/PlatformsContext';
import { PlatformLayout } from '@/platforms/PlatformLayout';
import { SIX_SECTIONS } from '@/platforms/types';
import { pagesFor, retailSectionOf, RETAIL_BASE } from './retailPages';

export function RetailArea(): JSX.Element {
  const { status } = usePlatforms();
  const st = status.retail;

  if (st.kind === 'loading') return <div className="loading-inline">Checking your Retail admin rights…</div>;
  if (st.kind !== 'ready') {
    return <EmptyState title="You have no admin rights in Retail Operations.">If you expect them, ask a Global Admin.</EmptyState>;
  }

  return (
    <PlatformLayout
      title="Retail Operations"
      basePath={RETAIL_BASE}
      enabled={new Set<string>(st.caps.sections.map((s) => String(s.id)))}
      sectionOf={retailSectionOf}
    />
  );
}

export function RetailSectionPage(): JSX.Element {
  const { section } = useParams();
  const { user } = useAuth();
  const def = SIX_SECTIONS.find((s) => s.id === section);
  const pages = pagesFor(user?.roles ?? []).filter((pg) => pg.section === section);

  if (!def) return <EmptyState title="There is no such section.">Choose a section from the menu.</EmptyState>;

  return (
    <div>
      <PageHeader title={def.label} intro={def.holds} />
      {pages.length === 0 && (
        <EmptyState title="Nothing here for your roles.">
          {def.id === 'find'
            ? 'Retail has no record lookup in the portal yet. Customer and application lookups stay on the record in the officer app.'
            : 'If you expect pages here, ask a Global Admin for the role.'}
        </EmptyState>
      )}
      <SectionPageCards pages={pages} />
    </div>
  );
}

/** A section's pages as cards, grouped under their headings (Retail and Loans section pages). */
export function SectionPageCards({
  pages,
}: {
  pages: ReadonlyArray<{ path: string; label: string; group: string; help?: string }>;
}): JSX.Element {
  const groups = new Map<string, Array<(typeof pages)[number]>>();
  for (const pg of pages) groups.set(pg.group, [...(groups.get(pg.group) ?? []), pg]);
  return (
    <>
      {[...groups.entries()].map(([group, items]) => (
        <section key={group} aria-label={group}>
          {groups.size > 1 && <h2 className="ps-group-h">{group}</h2>}
          <div className="ps-page-grid">
            {items.map((pg) => (
              <Link key={pg.path} to={pg.path} className="ps-page-card">
                <span className="ps-page-card__title">{pg.label}</span>
                {pg.help && <span className="ps-page-card__help">{pg.help}</span>}
              </Link>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

export { RETAIL_BASE };
